import { type FC, useRef } from "react";

import { m } from "@recobra/i18n/messages";
import {
  SelectableLiabilityDeclineReasonSchema,
  type SelectableLiabilityDeclineReason,
} from "@recobra/schemas/task";
import type { Resolution } from "@recobra/schemas/task-catalog";
import { Badge } from "@recobra/ui/badge";
import { DictationButton } from "@recobra/ui/custom/dictation-button";
import { Field, FieldLabel } from "@recobra/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupTextarea,
} from "@recobra/ui/input-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@recobra/ui/select";
import { Spinner } from "@recobra/ui/spinner";

import { sortByLabel } from "../../lib";
import { useTaskName } from "../task-data";
import type { TaskNotesSaveStatus } from "./useTaskNotes";

/**
 * Solution picker for task execution. A select rather than a card list so task
 * types with a long catalog stay compact and leave room for the checklist;
 * `disabled` covers solutions that follow from an action already taken.
 */
export const SolutionSelect: FC<{
  id: string;
  resolutions: Resolution[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ id, resolutions, value, onChange, disabled = false }) => (
  <Select
    value={value}
    onValueChange={(next) => onChange(next ?? "")}
    disabled={disabled}
    itemToStringLabel={(selected: string) =>
      resolutions.find((resolution) => resolution.id === selected)?.label ?? selected
    }
  >
    <SelectTrigger id={id} aria-label={m.task_outcome_select_label()} className="w-full">
      <SelectValue placeholder={m.task_solution_select_placeholder()} />
    </SelectTrigger>
    <SelectContent>
      {resolutions.map((resolution) => (
        <SelectItem key={resolution.id} value={resolution.id}>
          {resolution.label}
          {!resolution.active && (
            <Badge variant="outline">{m.task_solution_deprecated_badge()}</Badge>
          )}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** The task created once this one completes: the resolution's default, or another allowed one. */
export const NextTaskSelect: FC<{
  id: string;
  options: (string | null)[];
  value: string | null;
  onChange: (value: string | null) => void;
  disabled: boolean;
}> = ({ id, options, value, onChange, disabled }) => {
  const taskName = useTaskName();
  const label = (taskType: string | null) =>
    taskType ? taskName(taskType) : m.task_next_task_none();
  return (
    <Field>
      <FieldLabel htmlFor={id}>{m.task_next_task_label()}</FieldLabel>
      <Select
        value={value}
        onValueChange={(next) => onChange(next ?? null)}
        disabled={disabled}
        itemToStringLabel={label}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={m.task_next_task_none()} />
        </SelectTrigger>
        <SelectContent>
          {[
            ...options.filter((taskType) => taskType === null),
            ...sortByLabel(
              options.filter((taskType): taskType is string => taskType !== null),
              label,
            ),
          ].map((taskType) => (
            <SelectItem key={taskType ?? "none"} value={taskType}>
              {label(taskType)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
};

export const LiabilityDeclineReasonSelect: FC<{
  id: string;
  value: SelectableLiabilityDeclineReason | "";
  onChange: (value: SelectableLiabilityDeclineReason | "") => void;
  disabled: boolean;
}> = ({ id, value, onChange, disabled }) => (
  <Field>
    <FieldLabel htmlFor={id}>{m.task_decline_reason_label()}</FieldLabel>
    <Select
      value={value}
      onValueChange={(next) =>
        onChange(next ? SelectableLiabilityDeclineReasonSchema.parse(next) : "")
      }
      disabled={disabled}
      itemToStringLabel={(reason: string) => (reason ? m.task_decline_reason_name({ reason }) : "")}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder={m.task_decline_reason_placeholder()} />
      </SelectTrigger>
      <SelectContent>
        {sortByLabel(
          SelectableLiabilityDeclineReasonSchema.options,
          (reason) => m.task_decline_reason_name({ reason }),
          "other",
        ).map((reason) => (
          <SelectItem key={reason} value={reason}>
            {m.task_decline_reason_name({ reason })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </Field>
);

export const TaskCompletionNotes: FC<{
  notes: string;
  onNotesChange: (notes: string) => void;
  saveStatus: TaskNotesSaveStatus;
  solution: string;
  disabled?: boolean;
}> = ({ notes, onNotesChange, saveStatus, solution, disabled = false }) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <Field>
      <InputGroup>
        <InputGroupTextarea
          ref={textareaRef}
          value={notes}
          onChange={(event) => onNotesChange(event.target.value)}
          placeholder={m.task_outcome_notes_placeholder({ solution })}
          aria-label={m.task_outcome_notes_label()}
          rows={1}
          disabled={disabled}
        />
        <InputGroupAddon align="block-end">
          <div className="flex flex-1 items-center justify-between">
            <DictationButton
              targetRef={textareaRef}
              value={notes}
              onChange={onNotesChange}
              disabled={disabled}
            />
            <InputGroupText role="status" aria-live="polite">
              {saveStatus === "saving" && <Spinner aria-label={m.task_saving_changes()} />}
              {saveStatus === "saved" && m.task_notes_saved()}
              {saveStatus === "error" && m.task_notes_save_failed()}
            </InputGroupText>
          </div>
        </InputGroupAddon>
      </InputGroup>
    </Field>
  );
};
